import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { PageHeader } from '@platform/ui-kit';
import { api, type AirFitProjectGroup, type AirFitmentStandard } from '../../../api';
import './AiPlatformFit.css';

/**
 * Strategic Intelligence → AI Platform Fitness Analysis.
 *
 * RENAMED from "AI Platform Fit" on the owner's instruction, 2026-08-17 — menu label,
 * URL segment and page heading together, so the three cannot say different things.
 *
 * Ported from the source's `views/platforms/fit.ejs` and `routes/platform-fit.js` GET /.
 *
 * SELECTION DRIVES IT: tick a whole project or individual opportunities, then run. Each
 * analysis scores and ranks the platforms against the LOCKED fitment criteria and the
 * opportunity's own context, and writes a full enterprise justification. Opportunities are
 * analysed ONE AFTER ANOTHER and the panel names the one in flight.
 *
 * The source's two-column grid is kept: the selection tree on the left, the run status on
 * the right, so a long list can be scrolled without the status leaving the screen. Copy,
 * button labels, the Stop confirmation and the status wording are the source's.
 */
export function AiPlatformFit() {
  const [groups, setGroups] = useState<AirFitProjectGroup[]>([]);
  const [criteriaLocked, setCriteriaLocked] = useState(true);
  /* The three tabs of Platform Fitment Criteria, as the run will use them. */
  const [standard, setStandard] = useState<AirFitmentStandard | null>(null);
  const [showStandard, setShowStandard] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [filter, setFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const [jobId, setJobId] = useState<string | null>(null);
  const [job, setJob] = useState<{
    status: string; total: number; done: number; current: string | null;
    error: string | null; failedOn: string | null;
  } | null>(null);
  const [stopping, setStopping] = useState(false);

  /* The opportunity whose executive summary fills the right pane — the source's
     `pfShowExec` (fit.ejs:253), which reads it off the clicked row's data attributes. */
  const [picked, setPicked] = useState<AirFitProjectGroup['opps'][number] | null>(null);

  /* Collapse/expand a project, as `.pf-proj.open` does in the source. */
  const toggleOpen = (pid: string) =>
    setExpanded((cur) => {
      const n = new Set(cur);
      if (n.has(pid)) n.delete(pid);
      else n.add(pid);
      return n;
    });

  /*
   * The screen is locked and only `.pf-card-bd` scrolls. The source does this by overriding
   * `.v2-content` with `calc(100vh - 60px)`; this shell has no equivalent and CLAUDE.md
   * records that a percentage resolves against nothing here, so the height is measured.
   * A callback ref, because the tree mounts behind a loading gate.
   */
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);
  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );

  const load = useCallback(() => {
    setLoading(true);
    api.aiReadiness
      .getPlatformFit()
      .then((d) => {
        setGroups(d.projectGroups ?? []);
        setCriteriaLocked(d.criteriaLocked);
        setStandard(d.standard ?? null);
        setError(null);
      })
      .catch((e: Error) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  useEffect(load, [load]);

  const visible = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        opps: g.opps.filter(
          (o) =>
            o.opportunity_id.toLowerCase().includes(q) ||
            o.description.toLowerCase().includes(q) ||
            g.project_id.toLowerCase().includes(q) ||
            g.description.toLowerCase().includes(q),
        ),
      }))
      .filter((g) => g.opps.length);
  }, [groups, filter]);

  const toggleOpp = (id: string) =>
    setSelected((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });

  /* Ticking a project selects all its opportunities — and only the ones currently VISIBLE,
     so a filter narrows what "select the project" means rather than quietly including rows
     the user cannot see. */
  const toggleProject = (g: AirFitProjectGroup, on: boolean) =>
    setSelected((s) => {
      const n = new Set(s);
      g.opps.forEach((o) => (on ? n.add(o.opportunity_id) : n.delete(o.opportunity_id)));
      return n;
    });

  const poll = useCallback((id: string) => {
    api.aiReadiness
      .getFitStatus(id)
      .then((d) => {
        setJob(d);
        if (d.status === 'running') {
          timer.current = setTimeout(() => poll(id), 2000);
        } else {
          setStopping(false);
          load(); // scores and summaries have changed
        }
      })
      .catch(() => {
        timer.current = setTimeout(() => poll(id), 2000);
      });
  }, [load]);

  const run = () => {
    const ids = [...selected];
    if (!ids.length) return;
    setError(null);
    api.aiReadiness
      .runFit(ids)
      .then((d) => {
        setJobId(d.jobId);
        setJob({ status: 'running', total: d.total, done: 0, current: null, error: null, failedOn: null });
        timer.current = setTimeout(() => poll(d.jobId), 1200);
      })
      .catch((e: Error) => setError(e.message));
  };

  const stop = () => {
    if (!jobId) return;
    /* The source confirms, and says exactly what Stop costs: the one in flight is discarded,
       everything already finished is kept. */
    if (
      !window.confirm(
        'Stop the AI platform analysis?\n\n' +
          'The initiative currently being analyzed will be discarded and NOT saved. ' +
          'Everything already completed is kept.',
      )
    ) {
      return;
    }
    setStopping(true);
    api.aiReadiness.stopFit(jobId).catch((e: Error) => {
      setStopping(false);
      setError(e.message);
    });
  };

  /* `data-proj` on the source's row; here the group owns the opportunity, so look it up. */
  const pickedProject = picked
    ? (groups.find((g) => g.opps.some((o) => o.opportunity_id === picked.opportunity_id))
        ?.project_id ?? '')
    : '';

  const running = job?.status === 'running';

  return (
    <div className="pf-page" ref={setRootEl} style={rootH ? { height: rootH } : undefined}>
      <PageHeader title="AI Platform Fitness Analysis" />

      <div className="pf-body">
        {/* fit.ejs:133 — "Filter and the run action share one row, as on Platform Fit Summary." */}
        <div className="pf-toolbar">
          <div className="pf-search">
            <input
              type="text"
              placeholder="Filter by project or opportunity…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            />
          </div>
          <span style={{ flex: 1 }} />
          <span className="pf-selcount">{selected.size} selected</span>
          <button
            type="button"
            className="pf-run"
            onClick={run}
            disabled={
              !selected.size || running || !criteriaLocked || !(standard?.criteria.length ?? 0)
            }
          >
            <i className="material-icons">insights</i>Run AI Platform analysis
          </button>
          {running ? (
            <button type="button" className="pf-stop" onClick={stop} disabled={stopping}>
              {stopping ? 'Stopping…' : 'Stop analysis'}
            </button>
          ) : null}
        </div>

        {/* fit.ejs:143 — the source's own wording, verbatim. */}
        {!criteriaLocked ? (
          <div className="pf-banner">
            ⚠️ The <strong>Fitment Criteria are not locked</strong>. Set and lock them on the{' '}
            <a href="/ai_readiness/settings/ai-platform-selection/platform-fitment-criteria">
              Platform Fitment Criteria
            </a>{' '}
            screen before running an analysis — every analysis uses those locked criteria.
          </div>
        ) : null}

        {/* Owner-directed, 2026-08-17: the run is measured against all three tabs, so the
            screen states its own basis. Kept from the previous build; styled as a banner so
            it sits inside the source's layout rather than beside it. */}
        {standard && !standard.criteria.length ? (
          <div className="pf-banner">
            ⚠️ No criteria are selected on the <strong>Platform Capabilities</strong> tab, so
            there is nothing to score a platform against.
          </div>
        ) : null}
        {standard && standard.criteria.length && !standard.clientProfile.trim() ? (
          <div className="pf-banner">
            ⚠️ No client environment profile is set on the{' '}
            <strong>Organizational Readiness</strong> tab, so every platform scores 50 on that
            axis — {standard.weightReadiness}% of each overall score.
          </div>
        ) : null}

        {/* Owner-directed, 2026-08-17: the analysis is driven by all three tabs, so the
            screen states its basis. Kept as one compact line inside the source's layout
            rather than the MUI card it used to be. */}
        {standard && standard.criteria.length ? (
          <div className="pf-standard">
            <span>
              Measured against <strong>Platform Fitment Criteria</strong> —{' '}
              {standard.criteria.length} criteri{standard.criteria.length === 1 ? 'on' : 'a'} ·{' '}
              {standard.weightCapability}% capability / {standard.weightReadiness}% readiness
            </span>
            <button type="button" onClick={() => setShowStandard((v) => !v)}>
              {showStandard ? 'Hide' : 'Show'}
            </button>
          </div>
        ) : null}
        {standard && showStandard ? (
          <div className="pf-standard-detail">
            <div className="lbl">PLATFORM CAPABILITIES — weighted 1–5</div>
            <ul>
              {standard.criteria.map((c) => (
                <li key={c.id}>
                  <b>{c.weight}</b> {c.name}
                </li>
              ))}
            </ul>
            <div className="lbl">ORGANIZATIONAL READINESS</div>
            <ul>
              {standard.readinessFactors.map((f) => (
                <li key={f.name}>{f.name}</li>
              ))}
            </ul>
            {standard.clientProfile.trim() ? (
              <>
                <div className="lbl">CLIENT ENVIRONMENT PROFILE</div>
                <p>{standard.clientProfile}</p>
              </>
            ) : null}
          </div>
        ) : null}

        {error ? <div className="pf-banner">{error}</div> : null}

        <div className="pf-grid">
          {/* ── LEFT: Project / Opportunities selection — fit.ejs:147 ─────────────── */}
          <div className="pf-card">
            <div className="pf-card-hd">Project / Opportunities selection</div>
            <div className="pf-card-bd">
              {loading ? (
                <div className="pf-empty">Loading…</div>
              ) : !visible.length ? (
                <div className="pf-empty">
                  {groups.length
                    ? 'No project or opportunity matches that filter.'
                    : 'No opportunities found.'}
                </div>
              ) : (
                visible.map((g) => {
                  const analyzed = g.opps.filter((o) => o.analyzed).length;
                  const all = g.opps.every((o) => selected.has(o.opportunity_id));
                  const some = !all && g.opps.some((o) => selected.has(o.opportunity_id));
                  const open = expanded.has(g.project_id);
                  return (
                    <div className={`pf-proj${open ? ' open' : ''}`} key={g.project_id}>
                      <div className="pf-proj-hd" onClick={() => toggleOpen(g.project_id)}>
                        {/* The label stops propagation, as the source does, so ticking a
                            project does not also collapse it. */}
                        <label
                          style={{ display: 'flex', alignItems: 'center' }}
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className="pf-proj-chk"
                            checked={all}
                            ref={(el) => {
                              if (el) el.indeterminate = some;
                            }}
                            onChange={(e) => toggleProject(g, e.target.checked)}
                          />
                        </label>
                        <i className="material-icons caret">chevron_right</i>
                        <span className="pid">{g.project_id}</span>
                        {g.description ? <span className="pdesc">— {g.description}</span> : null}
                        <span className="count">
                          {g.opps.length} opp{g.opps.length === 1 ? '' : 's'}
                          {analyzed ? ` · ${analyzed} analyzed` : ''}
                        </span>
                      </div>

                      <div className="pf-opps">
                        {g.opps.map((o) => (
                          <div
                            className={`pf-opp${o.analyzed ? ' is-analyzed' : ''}`}
                            key={o.opportunity_id}
                          >
                            <input
                              type="checkbox"
                              className="pf-opp-chk"
                              checked={selected.has(o.opportunity_id)}
                              onChange={() => toggleOpp(o.opportunity_id)}
                            />
                            <div className="pf-opp-main" onClick={() => setPicked(o)}>
                              <div className="pf-opp-top">
                                <span className="oid">{o.opportunity_id}</span>
                                {o.analyzed ? (
                                  <span className="pf-status ok" title="Analysis complete">
                                    <i className="material-icons">check_box</i>Analyzed
                                  </span>
                                ) : (
                                  <span className="pf-status none" title="Not yet analyzed">
                                    <i className="material-icons">check_box_outline_blank</i>Not
                                    analyzed
                                  </span>
                                )}
                                {o.analyzed && o.bestPlatform ? (
                                  <span className="badge">✓ {o.bestPlatform}</span>
                                ) : null}
                                {o.overallScore != null ? (
                                  <span className="score all">Overall {o.overallScore}</span>
                                ) : null}
                                {o.fitScore != null ? (
                                  <span className="score fit">Fit {o.fitScore}</span>
                                ) : null}
                                {o.readinessScore != null ? (
                                  <span className="score rdy">Ready {o.readinessScore}</span>
                                ) : null}
                              </div>
                              {o.description ? (
                                <div className="odesc">{o.description}</div>
                              ) : null}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {/* ── RIGHT: executive summary / progress — fit.ejs:194 ──────────────────── */}
          <div className="pf-card">
            <div className="pf-card-hd">Executive summary &amp; run status</div>
            <div className="pf-card-bd">
              {/* `pfShowExec` — fit.ejs:253. Clicking an opportunity fills this pane. */}
              {!picked ? (
                <div className="pf-empty">
                  Select an opportunity on the left to read its executive summary.
                </div>
              ) : !picked.execSummary ? (
                <div className="pf-empty">
                  Opportunity {picked.opportunity_id} has not been analyzed yet. Select it and
                  run the analysis.
                </div>
              ) : (
                <div className="pf-exec">
                  <div className="hd">
                    <span className="star">★</span> Best fit — {picked.bestPlatform}{' '}
                    <span style={{ color: '#78909c', fontWeight: 400 }}>
                      ({picked.opportunity_id})
                    </span>
                  </div>
                  {picked.overallScore != null ||
                  picked.fitScore != null ||
                  picked.readinessScore != null ? (
                    <div className="scores">
                      {picked.overallScore != null ? (
                        <div className="s all">
                          <div className="n">{picked.overallScore}</div>
                          <div className="l">Overall</div>
                        </div>
                      ) : null}
                      {picked.fitScore != null ? (
                        <div className="s fit">
                          <div className="n">{picked.fitScore}</div>
                          <div className="l">Requirement fit</div>
                        </div>
                      ) : null}
                      {picked.readinessScore != null ? (
                        <div className="s rdy">
                          <div className="n">{picked.readinessScore}</div>
                          <div className="l">Adoption readiness</div>
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                  <div className="body">{picked.execSummary}</div>
                  <div className="meta">
                    Full 6-section analysis (incl. adoption considerations) is on the{' '}
                    <a
                      href={`/ai_readiness/strategic-intelligence/platform-fit-summary?open=${encodeURIComponent(
                        pickedProject,
                      )}&opp=${encodeURIComponent(picked.opportunity_id)}`}
                      style={{ color: '#0d47a1', fontWeight: 600 }}
                    >
                      Platform Fit Summary
                    </a>{' '}
                    screen.
                  </div>
                </div>
              )}

              {job ? (
                <div className="pf-progress">
                  {job.status === 'running'
                    ? `Analyzing ${job.done + 1} of ${job.total}${job.current ? ` — ${job.current}` : ''}`
                    : job.status === 'cancelled'
                      ? `Stopped. ${job.done} of ${job.total} completed and kept; the one in flight was discarded.`
                      : job.status === 'error'
                        ? `Failed on ${job.failedOn}: ${job.error}`
                        : `Done — ${job.done} of ${job.total} analyzed.`}
                  <div className="bar">
                    <span
                      style={{
                        width: job.total ? `${Math.round((job.done / job.total) * 100)}%` : '0%',
                      }}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
